// 多言語対応（i18n）のコア。国別ランキングでプレイヤーが多国籍になる前提の仕様。
//
// **カタログは（言語コード × ゲーム種別コード）で分ける**（仕様2）。
// ゲーム種別コードは新設せず **既存の `game_key`**（二角取り = `nikakudori`）を流用する。
// ポータル（ダッシュボード／登録モーダル）はゲームではないので `portal` を使う。
//   src/locales/<lang>/<catalog>.json   例: src/locales/en/nikakudori.json
//
// **翻訳対象**: 説明文・吹き出し・項目名ラベル・アイテム名・ダッシュボードUI。
// **対象外**: PNG画像のボタン（PLAY GAME / Home / NextGame 等＝翻訳には新規アートが要る）と
//             ビットマップフォントの英字見出し（GAME START / STALEMATE / SCORE BOARD 等）。
//             テキスト描画のボタン（確定・キャンセル等）は対象に含む。
//
// **読み込みは遅延**（Vite の `import.meta.glob`）＝選んだ言語のカタログだけ落ちてくるので、
// 言語を増やしても初期バンドルは太らない。言語追加は **JSON を1枚足すだけ**（コード変更なし）。

/** 対応言語コード。増やすときはここと `src/locales/<lang>/` の JSON を足す。 */
export const SUPPORTED_LANGS = ['ja', 'en'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

/** 権威ある原文の言語（訳が欠けたらここへフォールバックする）。 */
export const SOURCE_LANG: Lang = 'ja';

/**
 * 言語セレクタの表示名は**自言語表記**にする。
 * 日本語が読めない人が日本語のUIから自分の言語を選べるようにするため（ブートストラップ問題）。
 */
export const LANG_NAMES: Record<Lang, string> = {
  ja: '日本語',
  en: 'English',
};

/**
 * カタログ名。**ゲームのカタログは `game_key` と同じ値**を使う（種別コードを別に持たない）。
 * ゲームでないものは用途で名付ける: `portal`（ダッシュボード）/ `legal`（規約・ポリシー）。
 *
 * `legal` を分けてあるのは、法務文書が長文で**普段は誰も開かない**ため。
 * `portal` に混ぜると全員が起動時に条文を丸ごと落とすことになる。
 */
export const CATALOG_NAMES = [
  'portal',
  'legal',
  'nikakudori',
  'triangle-sweeper',
  'cards',
  '2048',
  'crates',
  'slide',
  'nonogram',
  'nanpure',
  'colorsort',
  'samegame',
  'mahjong',
  'kakuro',
  'numbersum',
  'pipes',
] as const;

/**
 * ⚠️ **ゲームを足したらここにも足す**。`main.ts` は `entry.key as CatalogName` と
 * キャストして渡すので、**足し忘れても型では止まらない**（2048 は実際に漏れていた）。
 * 値の配列から型を導いてあるので、抜けは `__tests__/catalogs.test.ts` が検出する。
 */
export type CatalogName = (typeof CATALOG_NAMES)[number];

export type MessageParams = Record<string, string | number>;
type Catalog = Record<string, string>;

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(v);
}

// ---------------------------------------------------------------- カタログの読み込み
// Vite が `src/locales/**/**.json` を個別チャンクに分けて、必要なものだけ fetch する。
// eager にしないのが肝（全言語をバンドルに入れない）。
const CATALOG_LOADERS = import.meta.glob<{ default: Catalog }>('../../locales/*/*.json');

const loaded = new Map<string, Catalog>(); // `${lang}/${catalog}` → メッセージ
let currentLang: Lang = SOURCE_LANG;

/**
 * **いま前面にあるカタログ**（＝最後に `loadLanguage` へ渡したもの）。参照はここを先に見る。
 *
 * 🔴 これが無いと**ゲームを2本続けて開いたときに前のゲームの文言が出る**
 *   （2026-08-16 に本番 iPad で発生。二角取り → 2048 の順に開くと 2048 の遊び方 1ページ目が
 *   二角取りの説明文になっていた）。ポータルは1オリジンの SPA なので `loaded` は
 *   **ゲームを跨いで貯まり続ける**。カタログ名を `game_key` で分けても、参照側が
 *   全カタログを舐めていては意味が無い＝**先に入った方が勝つ**。
 *   `game.howto.p1.body` / `game.title` / `game.hud.score` など**60キーが4本の間で重複**しており、
 *   ⚠️ 症状は「文言が違う」だけで例外もログも出ないため、遊んで気づくまで分からない。
 */
let activeCatalogs: readonly CatalogName[] = [];

const catalogPath = (lang: string, name: string): string => `../../locales/${lang}/${name}.json`;

async function loadOne(lang: Lang, name: CatalogName): Promise<void> {
  const cacheKey = `${lang}/${name}`;
  if (loaded.has(cacheKey)) return;
  const loader = CATALOG_LOADERS[catalogPath(lang, name)];
  if (!loader) return; // その言語のカタログが未整備＝フォールバックに任せる
  try {
    const mod = await loader();
    loaded.set(cacheKey, mod.default ?? (mod as unknown as Catalog));
  } catch {
    /* 読み込み失敗はフォールバックに任せる（原文が出るだけで機能は落ちない） */
  }
}

/**
 * 指定言語のカタログを読み込んで有効にする。**シーン生成前に必ず await する**
 * （`t()` は同期で、Phaser の `create()` から呼ばれるため）。
 * 原文言語も併せて読み、訳が欠けた行はそちらへフォールバックする。
 */
export async function loadLanguage(lang: Lang, catalogs: CatalogName[]): Promise<void> {
  currentLang = lang;
  // 渡された順がそのまま優先順位。**ゲームへ入るたびに前面が入れ替わる**（上の 🔴）。
  activeCatalogs = [...catalogs];
  const jobs: Promise<void>[] = [];
  for (const name of catalogs) {
    jobs.push(loadOne(lang, name));
    if (lang !== SOURCE_LANG) jobs.push(loadOne(SOURCE_LANG, name));
  }
  await Promise.all(jobs);
}

/** 現在の表示言語。 */
export function currentLanguage(): Lang {
  return currentLang;
}

// ---------------------------------------------------------------- 参照
/**
 * **前面のカタログを先に見てから**、残りの読み込み済みカタログを見る。
 *
 * 2段にしてあるのは、`portal` / `legal` のように**どのゲームからでも引きたいもの**が
 * あるため（前面はゲームのカタログ1つだけ）。前面に無いキーだけが後段へ落ちる。
 */
function lookup(lang: Lang, key: string): string | undefined {
  for (const name of activeCatalogs) {
    const hit = loaded.get(`${lang}/${name}`)?.[key];
    if (hit !== undefined) return hit;
  }
  for (const [cacheKey, catalog] of loaded) {
    if (!cacheKey.startsWith(`${lang}/`)) continue;
    // 前面ぶんは上で見たので飛ばす（同じカタログを2度見ない）。
    if (activeCatalogs.some((n) => cacheKey === `${lang}/${n}`)) continue;
    const hit = catalog[key];
    if (hit !== undefined) return hit;
  }
  return undefined;
}

/**
 * 単数/複数の出し分け。`count` を渡したときだけ `<key>_one` / `<key>_other` を先に探す。
 * 日本語には複数形が無いので `<key>` だけを置く運用でよい（英語だけ _one/_other を足す）。
 */
function resolve(key: string, count?: number): string | undefined {
  if (count !== undefined) {
    const suffixed = `${key}_${count === 1 ? 'one' : 'other'}`;
    const v = lookup(currentLang, suffixed) ?? lookup(SOURCE_LANG, suffixed);
    if (v !== undefined) return v;
  }
  return lookup(currentLang, key) ?? lookup(SOURCE_LANG, key);
}

/** `{name}` 形式のプレースホルダを埋める。未指定のものはそのまま残す（欠落に気づけるように）。 */
function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const v = params[name];
    return v === undefined ? whole : String(v);
  });
}

/**
 * メッセージを引く。訳が無ければ原文言語 → それも無ければ**キーをそのまま返す**
 * （画面に `game.bubble.swap` と出るので、訳し忘れが目で分かる）。
 */
export function t(key: string, params?: MessageParams): string {
  const count = typeof params?.count === 'number' ? params.count : undefined;
  const template = resolve(key, count);
  if (template === undefined) return key;
  return interpolate(template, params);
}

/**
 * 言語別のフォント指定（説明文・吹き出し等のテキスト描画用）。
 * ja/en は同じで足りるが、将来 CJK（中国語の字体差）やタイ語を足すときの差し込み口として
 * ここを唯一の窓口にしておく。※見出しはビットマップフォントの英字なので対象外。
 */
export function localeFont(): string {
  return 'sans-serif';
}
